import React from 'react';
import { Dropdown } from 'antd';
import { Tooltip } from '@mui/material';
import { DeleteOutlined } from '@ant-design/icons';
import { ReactComponent as OandaIcon } from '../../../assets/icons/oanda_icon.svg';
import { ReactComponent as BinanceIcon } from '../../../assets/icons/binance_icon.svg';
import { ReactComponent as YahooIcon } from '../../../assets/icons/yahoo_icon.svg';
import { ReactComponent as UnknownIcon } from '../../../assets/icons/unknown_icon.svg';
import { format, isToday, isYesterday } from 'date-fns';

const ChatItem = ({
  chat,
  isSelected,
  onChatSelect,
  instruments
}) => {
  const formatChatName = (chatName) => {
    if (!chatName) return 'Untitled';
    return chatName.replace('_', '/');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    try {
      if (isToday(date)) {
        return format(date, 'h:mm a');
      }
      if (isYesterday(date)) {
        return 'Yesterday';
      }
      return format(date, 'MMM d');
    } catch (error) {
      return '';
    }
  };

  const getProviderFromInstruments = (chatName) => {
    const formattedName = chatName?.replace('/', '_');
    for (const category of Object.values(instruments || {})) {
      if (formattedName in category) {
        return category[formattedName].provider;
      }
    }
    return null;
  };

  const getProvider = () => {
    return getProviderFromInstruments(chat.chat_name) || chat.provider || 'Unknown';
  };

  const getProviderIcon = () => {
    const provider = getProvider();

    switch (provider?.toUpperCase()) {
      case 'OANDA':
        return <OandaIcon className="provider-icon" />;
      case 'BINANCE':
        return <BinanceIcon className="provider-icon" />;
      case 'YAHOO':
        return <YahooIcon className="provider-icon" />;
      default:
        return <UnknownIcon className="provider-icon" />;
    }
  };

  // Check if chat has messages/conversation exists
  // A chat exists if it's not a temp chat and has been updated (has messages)
  const chatExists = () => {
    if (!chat.chat_id || chat.chat_id.startsWith('temp-')) {
      return false;
    }
    // If updated_at is different from created_at, it means messages were sent
    if (chat.created_at && chat.updated_at) {
      return new Date(chat.updated_at).getTime() !== new Date(chat.created_at).getTime();
    }
    // If we don't have timestamps, assume it exists if it's not a temp chat
    return true;
  };

  const handleDelete = (e) => {
    e.domEvent?.stopPropagation();
    if (chat.onDelete) {
      chat.onDelete(chat.chat_id, chat.chat_name);
    }
  };

  const menuItems = {
    items: [
      {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: handleDelete
      }
    ]
  };

  return (
    <div
      className={`chat-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onChatSelect(chat)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onChatSelect(chat)}
    >
      <div className="chat-item-details">
        <div className="chat-name-container">
          <Tooltip title={getProvider()} arrow>
            <span className="chat-provider-icon">
              {getProviderIcon()}
            </span>
          </Tooltip>
          <span className="chat-name">{formatChatName(chat.chat_name)}</span>
          {chatExists() && <span className="chat-exists-indicator" />}
        </div>
        <span className="chat-date">{formatDate(chat.updated_at)}</span>
      </div>
      <Dropdown
        menu={menuItems}
        trigger={['click']}
        placement="bottomRight"
      >
        <button
          className="chat-item-menu-button"
          onClick={(e) => e.stopPropagation()}
          aria-label="Delete chat"
        >
          <DeleteOutlined />
        </button>
      </Dropdown>
    </div>
  );
};

export default ChatItem;
